import { useMemo } from 'react';
import { useT } from '../lib/i18n';

// Two native <select>s — Month + Day — that render as the iOS scroll-wheel
// picker on iOS (Safari + WKWebView), a native dropdown sheet on Android,
// and a plain dropdown on desktop. All three "feel right" without us
// shipping a custom wheel component.
//
// Why native, not a hand-rolled wheel:
//   - iOS already does the wheel — and matches Contacts/Calendar exactly,
//     which is the muscle memory we're tapping into.
//   - Accessibility comes for free: VoiceOver, dynamic type, dark mode.
//   - The `inputMode="numeric"` text field that lived here before showed
//     a digit-only keypad with no "/" key, so users literally couldn't
//     type "06/14" on iPhone. That's the bug this component fixes.
//
// Year-less by policy (see migration 0062 header). The picker has no year
// column at all — there's no place for the year to live, by design.

const MONTH_PROBE_YEAR = 2000; // leap year so Feb 29 is selectable

/** Days in month for our year-less storage. Uses year 2000 (leap) so
 *  Feb is always 29. Anything outside 1-12 returns 31 as a safe default. */
function daysInMonth(month: number | null): number {
  if (month == null || month < 1 || month > 12) return 31;
  // Day-0 of next month = last day of this month. Standard trick.
  return new Date(MONTH_PROBE_YEAR, month, 0).getDate();
}

export function BirthdayPicker({
  month,
  day,
  onChange,
  disabled,
  language,
  ariaLabelMonth,
  ariaLabelDay,
}: {
  month: number | null;
  day: number | null;
  /** Fires on every change. Both values are independent — caller decides
   *  what to do when one is set and the other isn't (the DB CHECK
   *  + createClient/updateClient enforce both-or-neither, so a half-
   *  filled state is fine in the UI as long as the row is rejected at
   *  save time). The picker also auto-snaps day down when month changes
   *  to a shorter month (Mar 31 → Apr → snaps to Apr 30). */
  onChange: (month: number | null, day: number | null) => void;
  disabled?: boolean;
  language: 'en' | 'es';
  ariaLabelMonth?: string;
  ariaLabelDay?: string;
}) {
  const t = useT();
  const locale = language === 'es' ? 'es-US' : 'en-US';

  // Localized full month names — generated once per locale. Long form
  // ("January" / "enero") reads cleanly inside the iOS wheel; short
  // would feel cramped.
  const monthLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { month: 'long' });
    return Array.from({ length: 12 }, (_, i) =>
      fmt.format(new Date(MONTH_PROBE_YEAR, i, 1)),
    );
  }, [locale]);

  const maxDay = daysInMonth(month);
  // Day options follow the selected month. If month is unset, we still
  // offer 1-31 so the user can pick day first if they prefer.
  const dayOptions = useMemo(
    () => Array.from({ length: maxDay }, (_, i) => i + 1),
    [maxDay],
  );

  function handleMonthChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const raw = e.target.value;
    const next = raw === '' ? null : parseInt(raw, 10);
    if (next == null) {
      // User cleared month — clear day too so we don't leave a stray
      // "31" when the field "means" nothing.
      onChange(null, null);
      return;
    }
    // Snap day down if it doesn't exist in the new month (e.g. switching
    // from Jan 31 to Feb → snap to Feb 29).
    const cap = daysInMonth(next);
    const snappedDay = day != null && day > cap ? cap : day;
    onChange(next, snappedDay);
  }

  function handleDayChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const raw = e.target.value;
    const next = raw === '' ? null : parseInt(raw, 10);
    if (next == null) {
      // Clearing day clears the whole field. Half-populated rows are
      // rejected at save anyway; clearing both is cleaner UX than
      // leaving a stray month with no day.
      onChange(null, null);
      return;
    }
    onChange(month, next);
  }

  // Tailwind: matches the height + border treatment of the other Field
  // inputs in NewClientModal / ClientDetailModal so the row visually
  // belongs in the form. `appearance-none` removes the default Chrome
  // dropdown arrow on desktop; we draw our own chevron on top so iOS
  // and Android still get their native indicators (those don't honor
  // `appearance-none`).
  const baseSelectClasses =
    'w-full h-10 pl-3 pr-8 rounded-lg border border-line bg-surface text-ink-900 ' +
    'disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-navy-900 ' +
    'dark:focus:ring-gold-500 appearance-none';

  return (
    <div className="grid grid-cols-2 gap-2">
      <SelectWithChevron>
        <select
          value={month ?? ''}
          onChange={handleMonthChange}
          disabled={disabled}
          aria-label={ariaLabelMonth ?? t('birthdayPicker.aria.month')}
          className={baseSelectClasses}
        >
          <option value="">{t('birthdayPicker.placeholder.month')}</option>
          {monthLabels.map((label, i) => (
            <option key={i + 1} value={i + 1}>
              {label}
            </option>
          ))}
        </select>
      </SelectWithChevron>
      <SelectWithChevron>
        <select
          value={day ?? ''}
          onChange={handleDayChange}
          disabled={disabled}
          aria-label={ariaLabelDay ?? t('birthdayPicker.aria.day')}
          className={baseSelectClasses}
        >
          <option value="">{t('birthdayPicker.placeholder.day')}</option>
          {dayOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </SelectWithChevron>
    </div>
  );
}

/** Wraps a native <select> so we can overlay a chevron on the right side.
 *  The native chevron is hidden via `appearance-none` on the <select> so
 *  the form input matches the rest of our inputs on desktop. Mobile
 *  doesn't honor `appearance-none` for the wheel UI, which is exactly
 *  what we want — the wheel still feels native. */
function SelectWithChevron({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {children}
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
    </div>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
